import type { RuntimeInputPayload } from '@mintyads/shared-types';
import type {
  RuntimeProbeInputEvent,
  RuntimeProbeTransform,
} from './runtimeProbeTypes';

export interface RuntimeProbeClientPoint {
  readonly clientX: number;
  readonly clientY: number;
}

export type RuntimeProbeHitTargets = ReadonlyMap<string, {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}>;

export function clientPointToDesign(
  point: RuntimeProbeClientPoint,
  transform: RuntimeProbeTransform,
): { x: number; y: number } {
  return {
    x: (point.clientX - transform.canvasClientRect.x - transform.offsetX) / transform.scaleX,
    y: (point.clientY - transform.canvasClientRect.y - transform.offsetY) / transform.scaleY,
  };
}

export function designPointToClient(
  point: { readonly x: number; readonly y: number },
  transform: RuntimeProbeTransform,
): RuntimeProbeClientPoint {
  return {
    clientX: transform.canvasClientRect.x + transform.offsetX + point.x * transform.scaleX,
    clientY: transform.canvasClientRect.y + transform.offsetY + point.y * transform.scaleY,
  };
}

function hitLayerIds(
  point: RuntimeProbeClientPoint,
  hitTargets: RuntimeProbeHitTargets,
): string[] {
  const hits: string[] = [];
  for (const [id, rect] of hitTargets.entries()) {
    if (
      point.clientX >= rect.x
      && point.clientX <= rect.x + rect.width
      && point.clientY >= rect.y
      && point.clientY <= rect.y + rect.height
    ) {
      hits.push(id);
    }
  }
  return hits;
}

export function rawProbeInputFromClientPointer(input: {
  readonly type: RuntimeProbeInputEvent['type'];
  readonly phase: 'raw' | 'mapped' | 'rejected';
  readonly clientX: number;
  readonly clientY: number;
  readonly pointerId: number;
  readonly pointerType: string;
  readonly transform: RuntimeProbeTransform;
  readonly hitTargets: RuntimeProbeHitTargets;
}): RuntimeProbeInputEvent {
  const design = clientPointToDesign(input, input.transform);
  return {
    type: input.type,
    phase: input.phase,
    clientX: input.clientX,
    clientY: input.clientY,
    designX: design.x,
    designY: design.y,
    pointerId: input.pointerId,
    pointerType: input.pointerType,
    hitLayerIds: hitLayerIds(input, input.hitTargets),
    acceptedBy: [],
  };
}

export function probeInputFromRuntimeEvidence(input: {
  readonly payload: RuntimeInputPayload;
  readonly transform: RuntimeProbeTransform;
  readonly hitTargets: RuntimeProbeHitTargets;
}): RuntimeProbeInputEvent {
  const client = designPointToClient(
    { x: input.payload.designX, y: input.payload.designY },
    input.transform,
  );
  return {
    type: input.payload.gesture === 'pointer' ? 'pointermove' : input.payload.gesture,
    phase: 'accepted',
    clientX: client.clientX,
    clientY: client.clientY,
    designX: input.payload.designX,
    designY: input.payload.designY,
    pointerId: input.payload.pointerId,
    pointerType: input.payload.pointerType,
    hitLayerIds: hitLayerIds(client, input.hitTargets),
    acceptedBy: [`input.${input.payload.gesture}`],
    ...(input.payload.direction === undefined ? {} : { direction: input.payload.direction }),
  };
}

export interface RuntimeProbeInputBuffer {
  push(event: RuntimeProbeInputEvent): void;
  snapshot(): RuntimeProbeInputEvent[];
  clear(): void;
}

export function createRuntimeProbeInputBuffer(maxEvents = 120): RuntimeProbeInputBuffer {
  const events: RuntimeProbeInputEvent[] = [];
  const max = Math.max(1, maxEvents);
  return {
    push(event) {
      events.push(event);
      while (events.length > max) events.shift();
    },
    snapshot() {
      return [...events];
    },
    clear() {
      events.length = 0;
    },
  };
}
